import { bootMark } from '../boot-timing'
import type { BootActor } from './boot-provider'
import { toBootPhase } from './boot-machine'

/** The mark names a trace reads, in the order a clean boot emits them. */
export const BOOT_MARKS = {
    bundleStart: 'boot:bundle_start',
    whitelabelLoaded: 'boot:whitelabel_loaded',
    sessionValidated: 'boot:session_validated',
    userLoaded: 'boot:user_loaded',
    accessResolved: 'boot:access_resolved',
    ready: 'boot:ready',
} as const

/** Timing must never change boot behavior, so a failure to record is reported as `false` and nothing more. */
const safely = (record: () => void): boolean => {
    try {
        record()
        return true
    } catch {
        return false
    }
}

export const markBootStep = (name: (typeof BOOT_MARKS)[keyof typeof BOOT_MARKS], detail?: string): void => {
    safely(() => bootMark(name, detail))
}

let readyMarked = false

/** Marks the first render of the ready app for this page load. Later ready phases (a restored session) are not marked. */
export const markReadyRender = (): void => {
    if (readyMarked) {
        return
    }
    readyMarked = true
    markBootStep(BOOT_MARKS.ready)
}

export const resetBootMarkState = (): void => {
    readyMarked = false
}

/** The mark a boot transition produces, by the phase it leaves. */
const MARK_ON_LEAVING: Partial<Record<string, (typeof BOOT_MARKS)[keyof typeof BOOT_MARKS]>> = {
    identity: BOOT_MARKS.whitelabelLoaded,
    session: BOOT_MARKS.sessionValidated,
    access: BOOT_MARKS.accessResolved,
}

/**
 * Marks the boot phase boundaries as the actor moves. A boundary is marked only when the machine
 * advances, not when it fails or falls back to sign-in. Returns a function that stops listening.
 */
export const markBootPhases = (actor: BootActor): (() => void) => {
    let previous = toBootPhase(actor.getSnapshot()).phase
    const subscription = actor.subscribe((snapshot) => {
        const current = toBootPhase(snapshot).phase
        if (current === previous) {
            return
        }
        const advanced =
            (previous === 'identity' && current === 'session') ||
            (previous === 'session' && current === 'access') ||
            (previous === 'access' && current === 'ready')
        const mark = advanced ? MARK_ON_LEAVING[previous] : undefined
        previous = current
        if (mark) {
            markBootStep(mark)
        }
    })
    return () => subscription.unsubscribe()
}
